import React from 'react';
import { throttle } from 'underscore';

const DEFAULT_THROTTLE_MS = 5;

interface UseThrottledIntegerEmitConfig {
  /**
   * Called with the rounded integer value. Throttled and deduped so it only
   * fires when the rounded value actually changes.
   */
  onEmit: (val: number) => void;
  throttleMs?: number;
}

interface UseThrottledIntegerEmit {
  /** Feed a value. Skips if rounded value matches the last one. */
  update: (val: number) => void;
  /** Forget the last seen value so the next `update` always dispatches. */
  reset: () => void;
}

/**
 * Throttled emitter with integer-px dedupe. Rounds incoming values and only
 * dispatches when the rounded value changes, throttling the downstream call.
 */
const useThrottledIntegerEmit = ({
  onEmit,
  throttleMs = DEFAULT_THROTTLE_MS,
}: UseThrottledIntegerEmitConfig): UseThrottledIntegerEmit => {
  const lastValueRef = React.useRef<number | null>(null);

  const throttledEmit = React.useMemo(
    () =>
      throttle((val: number): void => {
        onEmit(val);
      }, throttleMs),
    [onEmit, throttleMs],
  );

  const update = React.useCallback(
    (val: number): void => {
      const rounded = Math.round(val);

      if (lastValueRef.current === rounded) {
        return;
      }

      lastValueRef.current = rounded;

      throttledEmit(rounded);
    },
    [throttledEmit],
  );

  const reset = React.useCallback((): void => {
    lastValueRef.current = null;
  }, []);

  return {
    update,
    reset,
  };
};

export default useThrottledIntegerEmit;
